import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';

interface HeaderProps {
  setSidebarOpen: (open: boolean) => void;
  onOpenCommandPalette?: () => void;
  onOpenCopilot?: () => void;
}

const NewHeader: React.FC<HeaderProps> = ({ setSidebarOpen, onOpenCommandPalette, onOpenCopilot }) => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Derive page title from current route
  const getPageTitle = (): string => {
    const path = location.pathname;
    const titles: Record<string, string> = {
      '/dashboard': 'Home',
      '/campaigns': 'Campaigns',
      '/whatsapp-dashboard': 'WhatsApp',
      '/whatsapp-onboarding': 'WhatsApp Setup',
      '/whatsapp-templates': 'Templates',
      '/lead-management': 'Leads',
      '/proposal-automation': 'Proposals',
      '/email-integration': 'Email',
      '/journey-orchestration': 'Journeys',
      '/flows': 'Automations',
      '/flow-builder': 'Flow Builder',
      '/workflow-automation': 'Workflows',
      '/inventory-management': 'Products',
      '/revenue-optimizer': 'Revenue AI',
      '/shopper-concierge': 'Concierge',
      '/support/tickets': 'Support',
      '/vector-database': 'Knowledge Base',
      '/business-intelligence': 'Customer Insights',
      '/analytics': 'Analytics',
      '/usage-dashboard': 'Usage',
      '/reports': 'Reports',
      '/brands': 'Brand Settings',
      '/integrations': 'Integrations',
      '/team-management': 'Team',
      '/account-management': 'Billing',
      '/brand-copilot': 'AI Copilot',
    };
    return titles[path] || 'AI Commerce OS';
  };

  // Get lifecycle stage color for breadcrumb
  const getStageColor = (): string => {
    const path = location.pathname;
    if (['/campaigns', '/whatsapp-dashboard', '/whatsapp-onboarding', '/whatsapp-templates', '/lead-management', '/proposal-automation', '/email-integration'].includes(path))
      return 'text-violet-600 bg-violet-50';
    if (['/journey-orchestration', '/flows', '/flow-builder', '/workflow-automation', '/inventory-management', '/revenue-optimizer', '/shopper-concierge'].includes(path))
      return 'text-blue-600 bg-blue-50';
    if (['/support/tickets', '/vector-database', '/business-intelligence'].includes(path))
      return 'text-emerald-600 bg-emerald-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getStageName = (): string | null => {
    const path = location.pathname;
    if (['/campaigns', '/whatsapp-dashboard', '/whatsapp-onboarding', '/whatsapp-templates', '/lead-management', '/proposal-automation', '/email-integration'].includes(path))
      return 'Reach';
    if (['/journey-orchestration', '/flows', '/flow-builder', '/workflow-automation', '/inventory-management', '/revenue-optimizer', '/shopper-concierge'].includes(path))
      return 'Convert';
    if (['/support/tickets', '/vector-database', '/business-intelligence'].includes(path))
      return 'Retain';
    return null;
  };

  const stageName = getStageName();

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="flex items-center justify-between h-14 px-4 lg:px-6">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          {/* Mobile menu */}
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 lg:hidden p-1"
            onClick={() => setSidebarOpen(true)}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>

          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-sm">
            {stageName && (
              <>
                <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${getStageColor()}`}>
                  {stageName}
                </span>
                <svg className="w-4 h-4 text-gray-300" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </>
            )}
            <h1 className="text-sm font-semibold text-gray-900">{getPageTitle()}</h1>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-2">
          {/* Command palette trigger */}
          <button
            onClick={onOpenCommandPalette}
            className="hidden sm:flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-400 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <span>Search...</span>
            <kbd className="hidden md:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono text-gray-400 bg-white border border-gray-200 rounded">
              Ctrl K
            </kbd>
          </button>

          {/* Copilot trigger */}
          <button
            onClick={onOpenCopilot}
            className="flex items-center space-x-1.5 px-3 py-1.5 text-sm font-medium text-violet-600 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
            </svg>
            <span className="hidden md:inline">AI Copilot</span>
          </button>

          {/* Notifications */}
          <button className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">
                  {user?.email?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
                </div>
                <Link to="/brands" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setShowUserMenu(false)}>
                  Brand Settings
                </Link>
                <Link to="/team-management" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setShowUserMenu(false)}>
                  Team
                </Link>
                <button
                  onClick={() => { signOut?.(); setShowUserMenu(false); }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default NewHeader;
